import type { Skill, SoundType } from '../../types';
import type { View, ViewContext } from '../router';
import { el, ensureStyle } from '../dom';
import { icon } from '../components/icons';
import {
  backBtn,
  emptyFocus,
  exitButton,
  ensureQuizStyle,
  etymologyLine,
  fillProgress,
  hearOthers,
  keyLegend,
  focusSetup,
  focusTag,
  lengthField,
  newBirdsField,
  markChoices,
  tagSlot,
  missedList,
  quizHeader,
  quizKeys,
  retryTag,
  afterBar,
  compareButton,
  dontKnowButton,
  undoButton,
  segmented,
  type HearHandle,
  type KeyActions,
  type Miss,
} from '../components/quiz-chrome';
import { waveformPlayer } from '../components/waveform-player';
import { photoCredit, speciesPhoto } from '../components/species-photo';
import {
  buildFieldSession,
  buildReviewSession,
  dueQueue,
  REVIEW_CAP,
  REVIEW_FLOOR,
  tally,
  type FieldQuestion,
} from '../../core/quiz/field';
import { focusCodes, parseFocus } from '../../core/quiz/focus';
import { WITHOUT_CALLS } from '../../core/quiz/sound';
import { choiceText, promptText } from '../../core/quiz/names';
import { isFakeName } from '../../core/quiz/fake-names';
import { heroMirrored } from '../../core/quiz/photo';
import { getSpecies } from '../../core/region';
import { studyCodes } from '../../core/ignored';
import { photosReady } from '../../core/media/image';
import { ratingFromCorrect } from '../../core/fsrs';
import { clearUndo, gradeAnswer, undoAnswer } from '../../core/undo';
import { reaskField } from '../../core/quiz/retry';
import { haptic } from '../haptics';
import { t } from '../../strings';
import {
  newBirdShare,
  retryMisses,
  ROUND_LENGTHS,
  roundLength,
  setNewBirdShare,
  setRoundLength,
  setSoundWithCalls,
  soundWithCalls,
} from '../../core/quiz/settings';
import {
  addXp,
  isDailyDone,
  markDailyDone,
  masteryBySkill,
  soundHardUnlocked,
  XP_DAILY_BONUS,
  XP_PER_CORRECT,
} from '../../core/progress';

ensureQuizStyle();
ensureStyle(
  'quiz-field-css',
  `
  /* The Field Test belongs to no single skill, so its chosen setup option takes
     the app's own accent rather than one of the three hues — picking one would
     say the round was mostly that. */
  .fieldq { --seg-on: var(--accent); }
  /* Which skill this question is. Not a spoiler and not decoration: the round
     changes shape question to question, and the tag is what says the shape you
     are looking at was dealt rather than chosen. It carries that skill's own
     hue, the one the study grid and the mastery rings already use.
     
     It rides the header row beside the counter (see .bar .tags in the shared
     chrome). It used to have a centred row of its own, which is a lot of a
     phone screen to spend on one word — and the word is a caption on the
     question, not a second question. */
  .fieldq .skilltag { color:var(--sk); border:1.5px solid currentColor;
    border-radius:999px; padding:3px 8px; font-size:8.4px; letter-spacing:.1em;
    line-height:1; flex:none; }
  .fieldq .hardtag { color:var(--accent); border:1.5px solid currentColor;
    border-radius:999px; padding:3px 8px; font-size:8.4px; letter-spacing:.1em;
    line-height:1; flex:none; }
  /* The name question's prompt: the common name, large, on a plain card. Same
     type as Name Match's, without the flip-card metrics — nothing flips here. */
  .fieldq .card { padding:26px 22px; text-align:center; }
  .fieldq .card .front { font-family:var(--font-display);
    font-variation-settings:var(--fvs-display); font-size:27px; line-height:1.2; }
  .fieldq .choice .nm { font-size:20px; line-height:1.2; }
  .fieldq .choice .nm.it { font-style:italic; }
  /* The tallest question in the round sets the answer list, and that is the
     photo one: a 4:3 hero, its credit, four choices AND Next have to share one
     phone screen, which at the shared quiz metrics they don't — the same
     squeeze Photo ID solved, and these are its numbers. Applied to every shape
     rather than only that one, because a list that changed row height from
     question to question would step up and down under the finger of someone
     answering at speed. */
  .fieldq .choices { gap:8px; margin-top:0; }
  .fieldq .choice { min-height:58px; padding:6px 52px 6px 16px; font-size:17px; }
  .fieldq .choice .sci { font-size:12px; margin-top:1px; }
  /* Under a made-up binomial that was picked: says it was never a bird. */
  .fieldq .choice .was { font-family:var(--font-body); font-style:normal; font-size:14px;
    margin-top:5px; opacity:.92; display:block; }
  @media (max-height:740px) {
    .fieldq .choice { min-height:52px; }
  }
  /* Only the sound questions grow a speaker on a wrong pick, so only they pay
     the gutter for one — see the .quiz.sound rules this mirrors. Set on the
     list rather than the view, because the next question may not want it. */
  .fieldq .choices.hearable .choice { padding-right:74px; }
  .fieldq .choices.hearable .choice.wrong::after { right:42px; width:24px; height:24px; }
  /* ---- The scorecard ------------------------------------------------------
     What a mixed round knows that a single-skill round cannot: "7/10" hides
     three sounds all wrong behind four names all right, and those are two
     different evenings' work. */
  .fieldq .breakdown { max-width:320px; margin:22px auto 0; text-align:left;
    display:flex; flex-direction:column; gap:9px; }
  .fieldq .breakdown > .label { margin-bottom:-1px; }
  .fieldq .brow { display:flex; align-items:center; gap:9px; }
  .fieldq .brow .dot { flex:none; width:10px; height:10px; border-radius:999px;
    background:var(--sk); }
  .fieldq .brow .nm { font-family:var(--font-display); font-size:14.5px;
    line-height:1.2; flex:none; }
  .fieldq .brow .bar { flex:1; min-width:0; }
  .fieldq .brow .bar > i { background:var(--sk); }
  .fieldq .brow .sc { flex:none; font-size:13px; color:var(--ink-soft);
    font-variant-numeric:tabular-nums; }
  .fieldq .done .next { display:flex; width:100%; max-width:280px; margin:14px auto 0; }
`,
);

/** What a round is allowed to play — Sound ID's hard mode, kept in the mix. */
type Voices = 'songs' | 'all';

const VOICES: { value: Voices; label: string; note: string }[] = [
  { value: 'songs', label: t('quiz.voices.songs'), note: t('quiz.voices.songsNote') },
  { value: 'all', label: t('quiz.voices.all'), note: t('quiz.voices.allNote') },
];

/** The mode a question came from, named the way the rest of the app names it. */
const SKILL_LABEL: Record<Skill, string> = {
  sound: t('common.skill.sound'),
  names: t('common.skill.names'),
  photo: t('common.skill.photo'),
};

/** The same three, at chip length. */
const SKILL_TAG: Record<Skill, string> = {
  sound: t('common.skillShort.sound'),
  names: t('common.skillShort.names'),
  photo: t('common.skillShort.photo'),
};

export const quizFieldView: View = async (root: HTMLElement, ctx: ViewContext) => {
  // Whether calls are in the mix is the same preference Sound ID keeps,
  // deliberately: "am I ready for calls" is one question about one learner, and
  // having the field test answer it separately would mean setting it twice. The
  // mastery unlock decides the default the same way there, off the Sound ID
  // ring.
  const unlocked = soundHardUnlocked(await masteryBySkill(studyCodes()));
  const [callsDefault, savedLen, savedShare, withPhotos] = await Promise.all([
    soundWithCalls(unlocked),
    roundLength('field'),
    newBirdShare('field'),
    photosReady(),
  ]);

  let voices: Voices = callsDefault ? 'all' : 'songs';
  // How many questions this round asks; opens on whatever the last field test
  // was run with.
  let len = savedLen;
  // How much of the round is held for birds this learner has never met, as a
  // share of its length; see core/quiz/settings.ts.
  let share = savedShare;
  // Whether the round IN PROGRESS has calls in it. Fixed when that round was
  // built, so a question can never disagree with the recording it is playing.
  let hard = voices === 'all';
  // Photo questions need the pack; without it the round is sound and names.
  const skills: Skill[] = withPhotos ? ['sound', 'names', 'photo'] : ['sound', 'names'];
  /**
   * The same screen, dealt from the due queue instead of from a chosen length —
   * the round the home screen's "Due today" panel opens. It shares every line of
   * this view below the deal: a review question IS a field question, graded
   * against the same card, and giving it its own view would be this file twice.
   */
  const review = ctx.params.mode === 'review';
  // A round started from the Library is confined to the birds that were on
  // screen when its button was pressed — see core/quiz/focus.ts. Never on a
  // review round: that one is what you owe, and narrowing it would not make
  // the rest of the debt go away, only hide it.
  const focus = review ? null : parseFocus(ctx.params.focus);
  // The ceiling here is the focus itself rather than any one skill's pool:
  // Name Match can ask about any bird at all, so every focused bird is
  // reachable by some question.
  const pool = focus ? focusCodes(focus) : null;

  const wrap = el('div.quiz.fieldq', {});
  root.append(wrap);

  let session: FieldQuestion[] = [];
  // How many questions the round was dealt. Anything past it is a miss come
  // back for another go — see `retryMisses` — and is practice, not scored.
  let size = 0;
  let index = 0;
  let retryOn = false;
  let correctCount = 0;
  // One entry per question answered, in order — what the per-skill scorecard is
  // built from at the end.
  let results: boolean[] = [];
  // The species this round got wrong, in the order they came up — each carrying
  // the voice it was asked in, where it was asked by one.
  let missed: Miss[] = [];
  let disposed = false;
  let currentPlayer: { stop: () => void } | null = null;
  // One speaker per bird that isn't the answer — see hearOthers.
  let currentSpeakers: HearHandle[] = [];

  // One key listener for the whole visit, reading whatever the screen currently
  // offers. A listener added per render is a listener leaked per render.
  let keys: KeyActions | null = null;
  const releaseKeys = quizKeys(wrap, () => keys);

  /**
   * The screen every visit opens on: how long the round is, which voices it may
   * use, and — when the pack is missing — what the round will be short of.
   */
  function renderSetup(): void {
    keys = null;
    stopMedia();
    // A field round can ask the same bird once per skill, so a focus of three
    // birds reaches nine questions rather than three — and a drill on ONE bird
    // reaches three rather than being a round of one question. Only the length
    // ladder is raised by it; the note above still counts birds.
    const setup = focusSetup(
      focus,
      pool,
      len,
      t('quiz.field.setup.lacking'),
      (pool?.length ?? 0) * skills.length,
    );
    len = setup.len;
    wrap.replaceChildren(
      el('div.bar', {}, backBtn(ctx)),
      el(
        'div.setup',
        {},
        el('h1', {}, t('common.mode.field')),
        // No blurb. The three single-skill setup screens dropped theirs for the
        // same reason: the title and the options are the screen, and a sentence
        // explaining the deal read as a note about the scheduler.
        setup.note,
        setup.empty
          ? null
          : lengthField(
              len,
              (n) => {
                len = n;
              },
              setup.lengths,
            ),
        setup.empty ? null : newBirdsField(share, (v) => (share = v)),
        el(
          'div.field',
          {},
          el('div.prompt.label', {}, t('quiz.voices.label')),
          segmented(VOICES, voices, (v) => (voices = v)),
          // Only the sound questions read this, and saying so is what keeps it
          // from looking like a setting the whole round obeys.
          el('div.hint', {}, t('quiz.field.setup.recordingsHint')),
        ),
        withPhotos ? null : el('div.hint', {}, t('quiz.field.setup.noPhotos')),
        setup.empty
          ? emptyFocus(ctx)
          : el(
              'button.next.btn.btn-primary',
              { onclick: () => void start() },
              t('quiz.round.start'),
              icon('arrow-right', { size: 18 }),
            ),
      ),
    );
  }

  async function start(): Promise<void> {
    // A length that came off a focus is a fact about how many sparrows there
    // are, not a preference — remembering it would hand the next ordinary round
    // a number the learner never chose.
    if (ROUND_LENGTHS.includes(len)) await setRoundLength('field', len);
    await setNewBirdShare('field', share);
    await setSoundWithCalls(voices === 'all');
    hard = voices === 'all';
    session = await buildFieldSession(new Date(), Math.random, len, {
      skills,
      voices: hard ? undefined : WITHOUT_CALLS,
      focus: pool ?? undefined,
    });
    await dealt();
  }

  /**
   * A review round: whatever has come around, capped, mixed.
   *
   * It asks for no setup. The length is the queue's, not a choice, and the
   * voices are whatever Sound ID is already set to — a screen of options in
   * front of "the cards you owe today" would be three taps between a learner
   * and the one round the app actually wants them to do.
   */
  async function startReview(): Promise<void> {
    hard = callsDefault;
    session = await buildReviewSession(new Date(), Math.random, {
      skills,
      voices: hard ? undefined : WITHOUT_CALLS,
    });
    await dealt();
  }

  /** Reset the scorecard for a freshly dealt round and show its first question. */
  async function dealt(): Promise<void> {
    retryOn = await retryMisses();
    size = session.length;
    index = 0;
    correctCount = 0;
    results = [];
    missed = [];
    render();
  }

  function stopMedia(): void {
    currentPlayer?.stop();
    currentPlayer = null;
    for (const speaker of currentSpeakers) speaker.stop();
    currentSpeakers = [];
  }

  function render(): void {
    stopMedia();
    keys = null;
    if (disposed) return;
    wrap.replaceChildren();
    if (!session.length) return renderEmpty();
    if (index >= session.length) return renderDone();
    renderQuestion(session[index]);
  }

  /**
   * The question the header asks, which is the skill's own question.
   *
   * Terse on purpose: the prompt shares one row with the back arrow, this
   * question's badges and the counter, and "Which bird is this?" was a
   * sentence where an instruction would do.
   */
  function promptFor(fq: FieldQuestion): string {
    if (fq.skill === 'names') return t('quiz.round.nameInLatin');
    return t('quiz.round.nameTheBird');
  }

  /**
   * One question, in whichever of the three shapes the deal handed over.
   *
   * The three differ only in what is above the answer list — a waveform, a
   * photograph, or a common name — and in what a wrong pick is allowed to
   * offer afterwards. Everything below that is one code path on purpose: a
   * field test that graded, marked or advanced differently depending on the
   * skill would be three quizzes sharing a route.
   */
  function renderQuestion(fq: FieldQuestion): void {
    const q = fq.question;
    const head = quizHeader(ctx, index, session.length, promptFor(fq));
    const isNames = fq.skill === 'names';

    const tags = tagSlot(head);
    if (focus) tags.append(focusTag(focus, size));
    // A miss come back: answered for practice, never scored or undone.
    const comeback = index >= size;
    if (comeback) tags.append(retryTag());
    tags.append(
      el(
        `span.skilltag.label.sk-${fq.skill}`,
        { title: t('quiz.field.question.skillTitle', { skill: SKILL_LABEL[fq.skill] }) },
        SKILL_TAG[fq.skill],
      ),
    );

    // What the question is asked WITH, and the hint that belongs to it.
    const stimulus: HTMLElement[] = [];
    let replay: (() => void) | undefined;
    let typeTag: HTMLElement | null = null;

    if (fq.skill === 'sound') {
      const player = waveformPlayer(fq.question.audio);
      currentPlayer = player;
      void player.toggle(); // autoplay
      replay = () => void player.toggle();
      stimulus.push(player.element, el('div.hint', {}, t('quiz.round.replayHint')));
      if (hard) {
        tags.append(
          el(
            'span.hardtag.label',
            { title: t('quiz.round.withCallsTitle') },
            t('quiz.round.withCalls'),
          ),
        );
      }
      // Which of the bird's three voices this clip is. Hidden until the answer
      // is in — before it, naming the sound type narrows the field for free.
      const type = fq.question.audio.type;
      typeTag = el(
        'span.typetag.label',
        { title: t('quiz.round.recordingType', { type }) },
        t(`quiz.chrome.soundType.${type}`),
      );
      typeTag.hidden = true;
      tags.append(typeTag);
    } else if (fq.skill === 'photo') {
      const flip = heroMirrored(fq.question);
      const frame = speciesPhoto(fq.question.photo, {
        alt: t('quiz.round.photoAlt'),
        mirror: flip,
      });
      frame.element.classList.add('hero');
      // The credit names the photographer, never the bird, so it is safe to show
      // before the answer.
      stimulus.push(
        frame.element,
        el('div.attrib.note', {}, photoCredit(fq.question.photo, flip)),
      );
    } else {
      stimulus.push(
        el('div.card', {}, el('div.front', {}, promptText(q.target, 'toSci'))),
      );
    }

    const choices = el('div.choices', {});
    // Only a sound question can offer the bird you named out loud, so only it
    // reserves the room for the button that does.
    if (fq.skill === 'sound') choices.classList.add('hearable');
    const buttons: HTMLButtonElement[] = [];
    const rows: HTMLElement[] = [];
    let answered = false;
    // Whether answering sent this question to the back of the round.
    let requeued = false;

    for (const code of q.choices) {
      const sp = getSpecies(code);
      const btn = el(
        'button.choice.btn.btn-quiet',
        {},
        // A name question is answered with binomials, which carry the italic the
        // rest of the app gives a scientific name; the other two are answered
        // with the common name over its binomial.
        ...(isNames
          ? [el('div.nm.it', {}, choiceText(code, 'toSci'))]
          : [el('div', {}, sp?.common ?? code), el('div.sci', {}, sp?.sci ?? '')]),
      ) as HTMLButtonElement;
      buttons.push(btn);
      btn.addEventListener('click', () => void settle(code, btn));
      // Wrapped, so a wrong pick can grow a speaker beside it (see hearIt): the
      // choice itself is disabled the moment it is answered, and a disabled
      // button swallows every click inside it.
      const row = el('div.choicerow', {}, btn);
      rows.push(row);
      choices.append(row);
    }

    const nextBtn = el(
      'button.next.btn.btn-primary',
      {},
      t('quiz.round.next'),
      icon('arrow-right', { size: 18 }),
    ) as HTMLButtonElement;
    nextBtn.hidden = true;
    nextBtn.addEventListener('click', () => {
      clearUndo();
      index += 1;
      render();
    });

    const undoBtn = undoButton(async () => {
      if (!answered || !(await undoAnswer())) return;
      haptic('undo');
      // `results` is one entry per question in order, and it is what the
      // per-skill scorecard is built from — so the pop has to happen here and
      // not only in the two counters beside it.
      const was = results.pop();
      if (was) correctCount -= 1;
      else missed.pop();
      if (requeued) session.pop();
      render();
    });

    // Opened on whatever this question was about: the voice a sound question
    // played, the plumage a photo question showed, and the pair's own best
    // facet for a name question.
    const compare = compareButton();

    const dontKnow = dontKnowButton(() => settle(null, null));

    const actions: KeyActions = {
      choices: buttons,
      replay,
      next: nextBtn,
      undo: undoBtn,
      skip: dontKnow,
    };
    keys = actions;

    /** Everything answering does, whether a bird was named or not. */
    async function settle(code: string | null, btn: HTMLButtonElement | null): Promise<void> {
      if (answered) return;
      answered = true;
      currentPlayer?.stop();
      const correct = code === q.target.code;
      // A made-up binomial (name questions only) is wrong with no bird behind
      // it: nothing to compare, and nothing for the mix-up record.
      const fake = code !== null && isFakeName(code);
      const named = fake ? null : code;
      haptic(correct ? 'right' : 'wrong');
      requeued = !correct && retryOn;
      if (requeued) session.push(reaskField(fq));
      if (comeback) {
        // Nothing to tally.
      } else {
        results.push(correct);
        if (correct) correctCount += 1;
        else missed.push({ code: q.target.code, type: voiceOf(fq) });
      }
      dontKnow.hidden = true;
      markChoices(buttons, q.choices, btn ? buttons.indexOf(btn) : -1, q.target.code);
      // The bar has to count the question just answered, or the last question of
      // a round sits at 90% while its result is on screen.
      fillProgress(head, index, session.length);
      // A name question is answered with binomials, so the gloss goes under the
      // right one, now that picking it is no longer possible. The other two
      // shapes are answered with common names and get none: this is a note on
      // the Latin, not a fact about the bird.
      if (isNames) {
        const etym = etymologyLine(q.target);
        if (etym) buttons[q.choices.indexOf(q.target.code)].append(etym);
        if (fake && btn) btn.append(el('div.was', {}, t('quiz.round.madeUp')));
      }
      if (typeTag) typeTag.hidden = false;
      // Nothing to hold this bird against when no bird was named.
      if (named) {
        compare.offer(
          q.target.code,
          named,
          fq.skill === 'sound'
            ? fq.question.audio.type
            : fq.skill === 'photo'
              ? 'photo'
              : undefined,
        );
      }
      if (fq.skill === 'sound') {
        // Every bird that wasn't the answer, in the voice they were just played
        // — silent until one is asked for.
        currentSpeakers = hearOthers(
          rows,
          q.choices,
          q.target.code,
          fq.question.audio.type,
          code ?? undefined,
        );
      }
      // `code` — the bird they named, right or wrong — grades the ordinary card
      // for this question's own skill, and feeds the mix-up list with it. A skip
      // names none, and is deliberately absent from that record: not knowing is
      // not confusing.
      if (!comeback) {
        await gradeAnswer({
          code: q.target.code,
          skill: fq.skill,
          rating: ratingFromCorrect(correct),
          picked: named ?? undefined,
          xp: correct ? XP_PER_CORRECT : 0,
        });
      }
      if (!disposed) {
        nextBtn.hidden = false;
        undoBtn.hidden = comeback;
      }
      nextBtn.replaceChildren(
        index + 1 >= session.length
          ? t('quiz.round.finish')
          : t('quiz.round.next'),
        icon('arrow-right', { size: 18 }),
      );
    }

    wrap.append(
      head,
      ...stimulus,
      choices,
      dontKnow,
      keyLegend(actions),
      afterBar(undoBtn, nextBtn, compare.element),
    );
  }

  /**
   * The voice a question was asked in, for the Missed panel to play back.
   *
   * Only a sound question has one. A photo or a name miss falls back to the
   * song, which is what `missedList` does with an absent type — a bird you
   * could not name off a photograph is still one worth hearing.
   */
  function voiceOf(fq: FieldQuestion): SoundType | undefined {
    return fq.skill === 'sound' ? fq.question.audio.type : undefined;
  }

  function renderEmpty(): void {
    // An empty review queue is the good outcome — every card the scheduler
    // wanted back today is back — so it reads as finished, not as broken.
    wrap.append(
      el('div.bar', {}, backBtn(ctx)),
      el(
        'div.done',
        {},
        el(
          'div.big',
          {},
          review ? t('quiz.field.empty.nothingDue') : t('quiz.round.nothingToStudy'),
        ),
        el(
          'div.stat',
          {},
          review
            ? t('quiz.field.empty.reviewStat')
            : hard
              ? t('quiz.round.noSpecies')
              : t('quiz.round.noSongs'),
        ),
        review
          ? el(
              'button.next.btn.btn-primary',
              { onclick: () => ctx.navigate('/home') },
              t('quiz.chrome.exit.home'),
            )
          : null,
        el(
          'button.next.btn.btn-quiet',
          { onclick: () => renderSetup() },
          review ? t('quiz.field.empty.fieldTestInstead') : t('quiz.round.changeOptions'),
        ),
      ),
    );
  }

  /** The scorecard: how each skill in the mix actually did. */
  function breakdown(): HTMLElement {
    const rows = tally(session.slice(0, size), results).map((row) => {
      const pct = Math.round((row.right / row.asked) * 100);
      return el(
        `div.brow.sk-${row.skill}`,
        {
          title: t('quiz.field.done.skillTitle', {
            right: row.right,
            asked: row.asked,
            skill: SKILL_LABEL[row.skill],
          }),
        },
        el('span.dot', {}),
        el('span.nm', {}, SKILL_LABEL[row.skill]),
        el('div.bar.meter', {}, el('i', { style: { width: `${pct}%` } })),
        el('span.sc', {}, t('quiz.field.done.skillScore', { right: row.right, asked: row.asked })),
      );
    });
    return el('div.breakdown', {}, el('span.label', {}, t('quiz.field.done.breakdown')), ...rows);
  }

  function renderDone(): void {
    const pct = Math.round((correctCount / size) * 100);
    // What the big button does is settled by `finishReview` a moment after the
    // scorecard paints — see there for why it is a swap rather than an unhide.
    let follow: () => void = () => void (review ? startReview() : start());
    const again = el(
      'button.next.btn.btn-primary',
      { onclick: () => follow() },
      review ? t('quiz.field.done.nextBatch') : t('quiz.round.again'),
      icon('arrow-right', { size: 18 }),
    ) as HTMLButtonElement;
    // A review round's follow-on exists only if the queue is still holding
    // something, and that takes a read of storage to know. Blank rather than
    // absent until it comes back: the button keeps its box either way, so the
    // scorecard never grows a row under a thumb already on its way down.
    if (review) again.style.visibility = 'hidden';
    const home = exitButton(ctx, Boolean(focus));
    keys = { next: again };
    // The day's bonus lands here in review mode, and says so in the line that
    // already reports the round's XP.
    const xp = correctCount * XP_PER_CORRECT;
    const stat = el('div.stat', {}, t('quiz.round.stat', { pct, xp }));
    // The misses play where they stand, in the voice they were asked in; their
    // speakers join the round's own and are hushed by the same stopMedia.
    const panel = missedList(missed, ctx);
    if (panel) currentSpeakers = panel.speakers;
    const correctLine = t('quiz.round.correct', {
      scored: correctCount,
      total: size,
    });
    wrap.append(
      el('div.bar', {}, backBtn(ctx)),
      el(
        'div.done',
        {},
        el('div.big', {}, review ? t('quiz.field.done.queueCleared') : correctLine),
        review ? el('div.stat', {}, correctLine) : null,
        stat,
        breakdown(),
        panel?.element ?? null,
        again,
        review
          ? null
          : el(
              'button.next.btn.btn-quiet',
              { onclick: () => renderSetup() },
              t('quiz.round.changeOptions'),
            ),
        home,
      ),
    );
    if (review) void finishReview(stat, pct, xp, again, home, (fn) => (follow = fn));
  }

  /**
   * The two things a finished review round can only know after asking storage:
   * whether it earned the day's bonus, and whether the queue has more waiting.
   *
   * Both are painted in rather than awaited before the scorecard, so the round's
   * result is on screen the instant the last question is answered.
   */
  async function finishReview(
    stat: HTMLElement,
    pct: number,
    xp: number,
    again: HTMLButtonElement,
    home: HTMLElement,
    setFollow: (fn: () => void) => void,
  ): Promise<void> {
    // Once per calendar day, for the first review round finished — the bonus the
    // daily challenge used to pay, moved onto the thing worth rewarding.
    const bonus = (await isDailyDone()) ? 0 : XP_DAILY_BONUS;
    if (bonus) {
      await markDailyDone();
      await addXp(bonus);
    }
    const left = (await dueQueue(new Date(), { skills })).total;
    if (disposed) return;
    if (bonus) stat.textContent = t('quiz.field.done.statWithBonus', { pct, xp, bonus });
    // The same floor the home panel keeps: a tail of one or two cards is not a
    // second round, and offering one here would put back exactly the sitting
    // Home has decided not to ask for. Those cards keep.
    if (left >= REVIEW_FLOOR) {
      again.title = t('quiz.field.done.stillDue', { left, next: Math.min(left, REVIEW_CAP) });
    } else {
      // Nothing more is due, so the big button becomes the way out rather than
      // vanishing. Vanishing is what put a learner on a random species page:
      // several batches in, "Next batch" is a place on the screen as much as a
      // label, and on the last one — the shortest round, and the one most
      // likely to have grown a list of misses above it — that place had a link
      // to a missed bird sitting under it.
      setFollow(() => ctx.navigate('/home'));
      again.replaceChildren(t('quiz.chrome.exit.home'));
      again.title = t('quiz.field.done.allBack');
      home.hidden = true;
    }
    again.style.visibility = '';
  }

  // `?go=1` — a round started from the Home card or from "Drill this bird",
  // both of which have already said everything the setup screen would ask.
  if (review) await startReview();
  else if (ctx.params.go) await start();
  else renderSetup();
  return () => {
    disposed = true;
    releaseKeys();
    // Leaving a round mid-answer takes the undo with it: the question it
    // belonged to is off the screen, so nothing may reach back to it.
    clearUndo();
    stopMedia();
  };
};
